import React from 'react';
import {
  X, Printer, Download, Columns, Maximize2, Minimize2,
  RefreshCw, FileText, CheckCircle2, AlertTriangle, Layers,
  ChevronDown, Plus, Sparkles, Filter, Database, ArrowLeft,
  Building2, Calendar, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

export interface OpenReportTab {
  id: string;
  title: string;
  icon: any;
  color: string;
  badge?: string;
  data: any;
  loading: boolean;
  error?: string | null;
  generatedAt: Date;
  filterEscolaId?: string;
  filterPeriodo?: string;
  filterDataInicio?: string;
  filterDataFim?: string;
  dimensions?: string[];
  metrics?: string[];
}

export interface EscolaItem {
  id: string;
  nome: string;
}

interface ReportWorkbenchProps {
  openTabs: OpenReportTab[];
  activeTabId: string | null;
  escolas?: EscolaItem[];
  onSelectTab: (id: string) => void;
  onCloseTab: (id: string) => void;
  onCloseAllTabs: () => void;
  onRefreshTab: (id: string) => void;
  onFilterChange?: (tabId: string, filters: { escolaId?: string; periodo?: string; dataInicio?: string; dataFim?: string }) => void;
  isSplitMode: boolean;
  onToggleSplitMode: () => void;
  secondaryTabId: string | null;
  onSelectSecondaryTab: (id: string) => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
  onBackToCatalog?: () => void;
}

export function exportReportToCsv(data: any, fileName: string) {
  if (!data?.colunas || !data?.linhas) {
    toast.error('Não há dados estruturados para exportar.');
    return;
  }
  try {
    const colHeaders = data.colunas.map((c: string) => `"${String(c).replace(/"/g, '""')}"`).join(';');
    const rowLines = data.linhas.map((row: any[]) =>
      row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(';')
    ).join('\n');
    const csvContent = '\uFEFF' + colHeaders + '\n' + rowLines;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const safeTitle = (fileName || 'relatorio').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    link.setAttribute('download', `${safeTitle}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Relatório exportado em CSV com sucesso!');
  } catch {
    toast.error('Erro ao gerar arquivo CSV.');
  }
}

export function ReportWorkbench({
  openTabs,
  activeTabId,
  escolas = [],
  onSelectTab,
  onCloseTab,
  onCloseAllTabs,
  onRefreshTab,
  onFilterChange,
  isSplitMode,
  onToggleSplitMode,
  secondaryTabId,
  onSelectSecondaryTab,
  isMaximized,
  onToggleMaximize,
  onBackToCatalog,
}: ReportWorkbenchProps) {
  if (openTabs.length === 0) return null;

  const activeTab = openTabs.find(t => t.id === activeTabId) || openTabs[0];
  const secondaryTab = openTabs.find(t => t.id === secondaryTabId) || openTabs.find(t => t.id !== activeTab?.id) || activeTab;

  return (
    <div className={`rpt-workbench ${isMaximized ? 'rpt-workbench-maximized' : ''}`}>
      {/* ─── BARRA DE ABAS SUPERIORES COM BOTÕES ÍCONES COM BORDA ─── */}
      <div className="rpt-wb-header">
        {onBackToCatalog && (
          <button
            type="button"
            className="rpt-wb-back-mobile"
            onClick={onBackToCatalog}
            title="Voltar ao catálogo de relatórios"
          >
            <ArrowLeft size={13} />
            <span>Catálogo</span>
          </button>
        )}

        <div className="rpt-wb-tabs-track">
          {openTabs.map(tab => {
            const isActive = tab.id === activeTab?.id;
            const Icon = tab.icon || FileText;
            return (
              <div
                key={tab.id}
                className={`rpt-wb-tab ${isActive ? 'rpt-wb-tab-active' : ''}`}
                onClick={() => onSelectTab(tab.id)}
                title={tab.title}
              >
                <div
                  className="rpt-wb-tab-icon"
                  style={{
                    background: isActive ? `${tab.color}25` : 'rgba(0,0,0,.06)',
                    color: isActive ? tab.color : 'inherit',
                  }}
                >
                  {tab.loading ? (
                    <RefreshCw size={11} className="rpt-spin-fast" />
                  ) : (
                    <Icon size={12} />
                  )}
                </div>
                <span className="rpt-wb-tab-title">{tab.title}</span>
                <button
                  type="button"
                  className="rpt-wb-tab-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseTab(tab.id);
                  }}
                  title="Fechar relatório"
                >
                  <X size={11} />
                </button>
              </div>
            );
          })}
        </div>

        {/* ─── BOTÕES DE AÇÃO COM BORDA (APENAS ÍCONES) ─── */}
        <div className="rpt-wb-controls">
          {openTabs.length >= 2 && (
            <button
              type="button"
              className={`rpt-wb-btn ${isSplitMode ? 'rpt-wb-btn-active' : ''}`}
              onClick={onToggleSplitMode}
              title={isSplitMode ? 'Desativar modo comparativo' : 'Comparar 2 relatórios lado a lado'}
            >
              <Columns size={14} />
            </button>
          )}

          <button
            type="button"
            className="rpt-wb-btn"
            onClick={() => exportReportToCsv(activeTab.data, activeTab.title)}
            title="Exportar dados da aba ativa em CSV"
            disabled={!activeTab?.data?.linhas?.length}
          >
            <Download size={14} />
          </button>

          <button
            type="button"
            className="rpt-wb-btn"
            onClick={() => window.print()}
            title="Imprimir ou salvar PDF da aba ativa"
            disabled={!activeTab?.data}
          >
            <Printer size={14} />
          </button>

          <button
            type="button"
            className={`rpt-wb-btn ${isMaximized ? 'rpt-wb-btn-active' : ''}`}
            onClick={onToggleMaximize}
            title={isMaximized ? 'Restaurar layout lateral' : 'Expandir relatório em tela cheia'}
          >
            {isMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>

          <button
            type="button"
            className="rpt-wb-btn rpt-wb-btn-danger"
            onClick={onCloseAllTabs}
            title="Fechar todos os relatórios abertos"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* ─── ÁREA DE CONTEÚDO (SINGLE VIEW OU DUAL SPLIT) ─── */}
      <div className={`rpt-wb-body ${isSplitMode ? 'rpt-wb-body-split' : ''}`}>
        {/* PAINEL PRINCIPAL (COLUNA 1) */}
        <div className="rpt-wb-panel">
          <ReportPanelView
            tab={activeTab}
            escolas={escolas}
            onRefresh={() => onRefreshTab(activeTab.id)}
            onFilterChange={(f) => onFilterChange?.(activeTab.id, f)}
            isSplit={isSplitMode}
          />
        </div>

        {/* PAINEL SECUNDÁRIO (COLUNA 2 - MODO SPLIT) */}
        {isSplitMode && (
          <div className="rpt-wb-panel rpt-wb-panel-secondary">
            <div className="rpt-wb-split-header">
              <span className="rpt-wb-split-label">Comparando com:</span>
              <select
                className="rpt-wb-split-select"
                value={secondaryTab.id}
                onChange={(e) => onSelectSecondaryTab(e.target.value)}
              >
                {openTabs.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.title} {t.id === activeTab.id ? '(Mesmo relatório)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <ReportPanelView
              tab={secondaryTab}
              escolas={escolas}
              onRefresh={() => onRefreshTab(secondaryTab.id)}
              onFilterChange={(f) => onFilterChange?.(secondaryTab.id, f)}
              isSplit={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPONENTE INTERNO: VISUALIZADOR DE DADOS DO RELATÓRIO COM FILTROS DE ESCOLA E PERÍODO ───
function ReportPanelView({
  tab,
  escolas = [],
  onRefresh,
  onFilterChange,
  isSplit,
}: {
  tab: OpenReportTab;
  escolas?: EscolaItem[];
  onRefresh: () => void;
  onFilterChange?: (filters: { escolaId?: string; periodo?: string; dataInicio?: string; dataFim?: string }) => void;
  isSplit?: boolean;
}) {
  const currentMonthName = new Date().toLocaleString('pt-BR', { month: 'long' });
  const currentYear = new Date().getFullYear();

  return (
    <div className="rpt-pv-container">
      {/* ─── CABEÇALHO DO PAINEL COM TÍTULO E STATUS ─── */}
      <div className="rpt-pv-header">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <h3 className="rpt-pv-title">{tab.data?.titulo || tab.title}</h3>
            {tab.data && (
              <span className="rpt-pv-pill">
                <Database size={10} /> {tab.data.linhas?.length || 0} registros
              </span>
            )}
            {tab.data?.periodo && (
              <span className="rpt-pv-periodo-pill">
                <Clock size={10} /> {tab.data.periodo}
              </span>
            )}
          </div>
          <div className="rpt-pv-date">
            Data de referência: {tab.data?.data ? new Date(tab.data.data).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')} · Gerado em {new Date(tab.generatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        <button
          type="button"
          className="rpt-pv-refresh-btn"
          onClick={onRefresh}
          title="Recarregar dados do servidor com filtros atuais"
        >
          <RefreshCw size={12} className={tab.loading ? 'rpt-spin-fast' : ''} />
        </button>
      </div>

      {/* ─── BARRA DE PARÂMETROS E FILTROS DINÂMICOS (ESCOLA + INTERVALO TEMPORAL) ─── */}
      <div className="rpt-pv-filters-bar">
        {/* Seletor de Escola */}
        <div className="rpt-pv-filter-group">
          <label className="rpt-pv-filter-lbl" htmlFor={`filter-escola-${tab.id}`}>
            <Building2 size={12} />
            <span>Escola:</span>
          </label>
          <select
            id={`filter-escola-${tab.id}`}
            className="rpt-pv-filter-select"
            value={tab.filterEscolaId || 'todas'}
            onChange={(e) => onFilterChange?.({ escolaId: e.target.value })}
            disabled={tab.loading}
          >
            <option value="todas">🌐 Todas as Escolas da Rede</option>
            {escolas.map((esc) => (
              <option key={esc.id} value={esc.id}>
                🏫 {esc.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Seletor de Período / Intervalo */}
        <div className="rpt-pv-filter-group">
          <label className="rpt-pv-filter-lbl" htmlFor={`filter-periodo-${tab.id}`}>
            <Calendar size={12} />
            <span>Período:</span>
          </label>
          <select
            id={`filter-periodo-${tab.id}`}
            className="rpt-pv-filter-select"
            value={tab.filterPeriodo || 'mes_atual'}
            onChange={(e) => onFilterChange?.({ periodo: e.target.value })}
            disabled={tab.loading}
          >
            <option value="hoje">📅 Hoje (Dia Atual)</option>
            <option value="ontem">📅 Ontem</option>
            <option value="7dias">📅 Últimos 7 dias</option>
            <option value="30dias">📅 Últimos 30 dias</option>
            <option value="mes_atual">📅 Mês Atual ({currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)})</option>
            <option value="mes_anterior">📅 Mês Anterior</option>
            <option value="ano_letivo">📅 Ano Letivo {currentYear}</option>
          </select>
        </div>
      </div>

      {/* ─── CORPO DE DADOS: LOADING / ERRO / DADOS ─── */}
      {tab.loading ? (
        <div className="rpt-pv-empty-state">
          <div className="rpt-spinner" />
          <div style={{ marginTop: 12, fontWeight: 600, fontSize: '.82rem' }}>
            Consultando banco de dados com filtros aplicados...
          </div>
          <div style={{ fontSize: '.68rem', opacity: .6, marginTop: 4 }}>
            Consolidando dados analíticos em tempo real
          </div>
        </div>
      ) : tab.error ? (
        <div className="rpt-pv-empty-state rpt-pv-error">
          <AlertTriangle size={24} style={{ color: '#ef4444' }} />
          <div style={{ marginTop: 8, fontWeight: 700, fontSize: '.84rem', color: '#ef4444' }}>
            {tab.error}
          </div>
          <button
            type="button"
            className="rpt-wb-btn"
            style={{ marginTop: 12, padding: '5px 12px', width: 'auto' }}
            onClick={onRefresh}
          >
            <RefreshCw size={12} /> Tentar novamente
          </button>
        </div>
      ) : tab.data ? (
        <>
          {/* KPI Cards de Resumo */}
          {Object.entries(tab.data.resumo || {}).filter(
            ([k, v]) => (typeof v === 'number' || typeof v === 'string') && k !== 'periodo'
          ).length > 0 && (
            <div className={`rpt-pv-kpis-grid ${isSplit ? 'rpt-pv-kpis-compact' : ''}`}>
              {Object.entries(tab.data.resumo || {})
                .filter(([k, v]) => (typeof v === 'number' || typeof v === 'string') && k !== 'periodo')
                .slice(0, 6)
                .map(([key, val]) => (
                  <div key={key} className="rpt-pv-kpi-card">
                    <span className="rpt-pv-kpi-num">
                      {typeof val === 'number' ? val.toLocaleString('pt-BR') : String(val)}
                    </span>
                    <span className="rpt-pv-kpi-lbl">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {/* Tabela de Linhas */}
          {tab.data.linhas?.length > 0 ? (
            <div className="rpt-pv-table-wrap">
              <table className="rpt-pv-table">
                <thead>
                  <tr>
                    {(tab.data.colunas || []).map((col: string, i: number) => (
                      <th key={i}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tab.data.linhas.map((row: any[], i: number) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{cell != null ? String(cell) : '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rpt-pv-empty-data">
              <FileText size={28} style={{ opacity: .3, margin: '0 auto 8px' }} />
              <div>Nenhum dado encontrado para o período ou filtros selecionados.</div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
